<template>
  <div :class="['min-h-screen bg-gray-50', isModal ? 'pb-0' : 'pb-60']">
    <!-- 顶部导航 -->
    <div v-if="!isModal" class="bg-white sticky top-0 z-10 border-b border-gray-200">
      <div class="flex items-center justify-between px-4 h-12">
        <div class="flex items-center" @click="$router.back()">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </div>
        <div class="text-lg font-medium text-gray-900">公章申请详情</div>
        <div class="w-6"></div>
      </div>
    </div>
    <div v-else class="bg-white sticky top-0 z-10 border-b border-gray-200">
       <div class="flex items-center justify-between px-4 h-12">
        <div class="w-6"></div>
        <div class="text-lg font-medium text-gray-900">公章申请详情</div>
        <div class="flex items-center" @click="$emit('close')">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <div v-else-if="error" class="p-4 text-center text-red-500">
      {{ error }}
    </div>

    <div v-else class="p-4 space-y-4">
      <!-- 状态卡片 -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-gray-500 text-sm">申请单号</span>
          <span class="text-gray-900 text-sm font-medium">{{ instanceInfo.businessNo || '-' }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-gray-500 text-sm">当前状态</span>
          <span :class="getStatusClass(currentStatusKey)" class="text-xs px-2 py-1 rounded-full">
            {{ currentStatusTextZh }}
          </span>
        </div>
      </div>

      <!-- 核心信息 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">基本信息</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">申请人</span>
            <span class="text-gray-900">{{ instanceInfo.submitterName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">申请日期</span>
            <span class="text-gray-900">{{ formatDate(instanceInfo.submittedAt) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">印章类型</span>
            <span class="text-gray-900">
              {{ getSealTypeZh(initialData.sealType) }}
              <span v-if="(initialData.sealType === '其他' || initialData.sealType === 'other') && initialData.otherSealType">
                ({{ initialData.otherSealType }})
              </span>
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">用印数量</span>
            <span class="text-gray-900">{{ initialData.quantity || '1' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">预计用印时间</span>
            <span class="text-gray-900">{{ formatDate(initialData.expectedDate) }}</span>
          </div>
        </div>
      </div>

      <!-- 详细信息 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">申请事由</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">紧急程度</span>
            <span class="text-gray-900">{{ getUrgencyText(initialData.urgencyLevel) }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-900 bg-gray-50 p-2 rounded min-h-[60px]">{{ initialData.reason || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 用印明细列表 -->
      <div v-if="sealList.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <div class="flex justify-between items-center border-b pb-2">
          <h3 class="font-medium text-gray-900">用印明细 ({{ sealList.length }})</h3>
          <button @click="toggleSealPanel" class="text-blue-600 text-sm">
            {{ isSealCollapsed ? '展开' : '收起' }}
          </button>
        </div>
        
        <div v-show="!isSealCollapsed" class="space-y-3">
          <div v-for="(item, idx) in sealList" :key="idx" 
            class="bg-gray-50 p-3 rounded text-sm border border-gray-100">
            <div class="flex justify-between mb-1">
              <span class="font-medium text-gray-900">{{ getSealTypeZh(item.sealType) }}</span>
              <span class="text-gray-500">x{{ item.quantity || item.sealQuantity || 1 }}</span>
            </div>
            <div v-if="item.sealType === 'other' || item.otherSealDescription" class="text-xs text-gray-500 mb-1">
               说明: {{ item.otherSealDescription || '-' }}
            </div>
            <div class="text-xs text-gray-600">
               用途: {{ item.purpose || item.sealReason || '-' }}
            </div>
          </div>
          
          <!-- 抄送人 -->
          <div v-if="ccUsersNormalized.length" class="relative pt-2">
             <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white bg-gray-400"></div>
             <div class="flex flex-col">
               <span class="text-sm font-medium text-gray-900">抄送</span>
               <div class="flex flex-wrap gap-1 mt-1">
                 <span v-for="(cc, idx) in ccUsersNormalized" :key="idx" 
                   class="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                   {{ cc.ccUserName || cc.name || cc }}
                 </span>
               </div>
             </div>
          </div>
        </div>
      </div>

      <!-- 申请备注 -->
      <div v-if="initialData.remarks" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">申请备注</h3>
        <div class="text-sm text-gray-900 bg-gray-50 p-2 rounded">
          {{ initialData.remarks }}
        </div>
      </div>

      <!-- 附件 -->
      <div v-if="attachmentsNormalized.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">附件</h3>
        <div class="space-y-2">
          <div v-for="(att, i) in attachmentsNormalized" :key="i" 
            class="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100">
            <div class="flex items-center space-x-3 overflow-hidden">
               <!-- 图片显示缩略图 -->
               <div v-if="isImage(att.url || att.fileName)" class="h-12 w-12 flex-shrink-0 overflow-hidden rounded border border-gray-200" @click="openImage(att.url)">
                 <img :src="sanitizeAttachmentUrl(att.url)" class="h-full w-full object-cover" alt="附件" />
               </div>
               <span class="text-sm text-gray-700 truncate flex-1">{{ att.fileName || '附件' }}</span>
            </div>
            <a v-if="!isImage(att.url || att.fileName)" :href="sanitizeAttachmentUrl(att.url)" target="_blank" class="text-blue-600 text-sm flex-shrink-0 ml-2">查看</a>
            <span v-else class="text-blue-600 text-sm flex-shrink-0 ml-2" @click="openImage(att.url)">查看大图</span>
          </div>
        </div>
      </div>

      <!-- 审批流程 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">审批流程</h3>
        <div class="relative pl-4 border-l-2 border-gray-200 space-y-6 my-2">
          <div v-for="(node, index) in workflowNodesPreview" :key="index" class="relative">
            <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white"
              :class="nodeNumberClass(node)"></div>
            <div class="flex flex-col">
              <div class="flex justify-between items-start">
                <span class="text-sm font-medium text-gray-900">{{ node.nodeName }}</span>
                <span class="text-xs text-gray-500" v-if="node.processedAt">{{ formatDate(node.processedAt) }}</span>
              </div>
              <div class="text-xs text-gray-600 mt-1">
                <span v-if="node.approvers && node.approvers.length">
                  {{ node.approvers.map(a => a.approverName).join(', ') }}
                </span>
                <span v-else>待定</span>
              </div>
              <div class="mt-1">
                 <span :class="actionBadgeClass(node)" class="text-xs px-2 py-0.5 rounded">
                   {{ node.actionText || getNodeStatusText(node.action) }}
                 </span>
              </div>
              <div v-if="node.remarks" class="mt-1 text-xs text-gray-500 bg-gray-50 p-1 rounded">
                {{ node.remarks }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div v-if="canApprove" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-50">
      <div class="flex flex-col space-y-3">
        <textarea v-model="approvalComment" rows="2" placeholder="请输入审批意见（可选）"
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
        <div class="flex space-x-3">
          <button @click="handleReject" :disabled="approving"
            class="flex-1 bg-red-50 text-red-600 py-2.5 rounded-lg font-medium active:bg-red-100 disabled:opacity-50">
            驳回
          </button>
          <button @click="handleApprove" :disabled="approving"
            class="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium active:bg-blue-700 shadow-sm shadow-blue-200 disabled:opacity-50">
            通过
          </button>
        </div>
      </div>
    </div>
    <ConfirmDialog
      v-model:visible="showNextApprovalDialog"
      title="操作成功"
      message="还有待办审批，是否继续审批下一条？"
      confirmText="继续审批"
      @confirm="handleNextApprovalConfirm"
      @cancel="goBack"
    />
    <FileViewer
      :visible="showFileViewer"
      :file="previewFile"
      @close="showFileViewer = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import FileViewer from '@/components/FileViewer.vue'
import { approvalApi } from '@/api/api.js'
import { toast } from '@/utils/toast.js'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  isModal: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'refresh'])

const route = useRoute()
const router = useRouter()
const applicationId = computed(() => props.id || route.params.id)

const showNextApprovalDialog = ref(false)
const pendingApprovals = ref([])

// 文件预览
const showFileViewer = ref(false)
const previewFile = ref({})

watch(applicationId, () => {
  loadDetail()
})

const goBack = () => {
  if (props.isModal) {
    emit('close')
  } else {
    router.back()
  }
}

const checkAndPromptNext = async () => {
  if (props.isModal) {
    emit('refresh')
    emit('close')
    return
  }
  try {
    const res = await approvalApi.getPendingApprovals({ page: 1, pageSize: 20, status: 'pending' })
    if (res.success && (res.data.list || res.data.items || []).length > 0) {
       pendingApprovals.value = res.data.list || res.data.items || []
       const currentId = applicationId.value
       const nextItems = pendingApprovals.value.filter(item => (item.id || item.instanceId) !== currentId)
       
       if (nextItems.length > 0) {
         pendingApprovals.value = nextItems
         showNextApprovalDialog.value = true
       } else {
         goBack()
       }
    } else {
       goBack()
    }
  } catch(e) {
    console.error(e)
    goBack()
  }
}

const handleNextApprovalConfirm = () => {
  showNextApprovalDialog.value = false
  const item = pendingApprovals.value[0]
  if (item) {
     const typeMap = {
        '付款申请': 'payment',
        '付款申请(小额)': 'payment',
        'payment': 'payment',
        '开票申请': 'invoice',
        'Invoice': 'invoice',
        'invoice': 'invoice',
        '公章申请': 'seal',
        '公章申请(合同)': 'seal',
        'seal': 'seal'
      };
      const typeKey = item.businessType || item.type || '';
      const type = typeMap[typeKey] || typeMap[Object.keys(typeMap).find(k => k.toLowerCase() === typeKey.toLowerCase())];
      const id = item.id || item.instanceId || item.businessKey;
      
      if (type && id) {
        router.replace(`/mobile/approval/${type}/${id}`)
      } else {
        goBack()
      }
  } else {
    goBack()
  }
}

const loading = ref(false)
const error = ref('')
const initialData = ref({})
const instanceInfo = ref({})
const approvalRecords = ref([])
const workflowNodes = ref([])
const approvalComment = ref('')
const approving = ref(false)
const isSealCollapsed = ref(true)

// Helpers
const toggleSealPanel = () => { isSealCollapsed.value = !isSealCollapsed.value }

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

const getSealTypeZh = (type) => {
  if (!type) return '-'
  const map = {
    'official': '公章',
    'contract': '合同章',
    'finance': '财务章',
    'invoice': '发票章',
    'legal': '法人章',
    'other': '其他'
  }
  return map[type.toLowerCase()] || type
}

const getUrgencyText = (level) => {
  const map = {
    'normal': '普通',
    'urgent': '紧急',
    'very_urgent': '特急',
    '1': '普通',
    '2': '紧急',
    '3': '特急'
  }
  return map[level] || '普通'
}

const sealList = computed(() => {
  if (initialData.value.sealList && Array.isArray(initialData.value.sealList)) {
    return initialData.value.sealList
  }
  if (initialData.value.sealType) {
    return [{
      sealType: initialData.value.sealType,
      otherSealDescription: initialData.value.otherSealDescription,
      quantity: initialData.value.quantity || initialData.value.sealQuantity || 1,
      purpose: initialData.value.reason || initialData.value.sealReason || initialData.value.sealPurpose
    }]
  }
  return []
})

const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  }
  return map[status] || 'bg-gray-100 text-gray-800'
}

// 提取文件名（支持 URL 或原始名称）
const extractFileNameFromUrl = (u) => {
  const s = String(u || '')
  const base = s.split('?')[0]
  const parts = base.split('/')
  return decodeURIComponent(parts.pop() || '附件')
}

// 统一附件结构为 { url, fileName }
const normalizeAttachment = (att) => {
  if (!att) return { url: '', fileName: '附件' }
  if (typeof att === 'string') {
    const url = sanitizeAttachmentUrl(att)
    return { url, fileName: extractFileNameFromUrl(url) }
  }
  const rawUrl =
    att.url ||
    att.fileUrl ||
    att.FileUrl ||
    att.FullUrl ||
    att.href ||
    att.path ||
    att.imageUrl ||
    att.link ||
    att.src ||
    att.file_path ||
    att.filePath ||
    ''
  const url = sanitizeAttachmentUrl(rawUrl || att.name || '')
  const fileName = att.fileName || att.file_name || att.name || extractFileNameFromUrl(url)
  return { url, fileName }
}

const attachmentsNormalized = computed(() => {
  const fromJson = Array.isArray(initialData.value?.attachments) ? initialData.value.attachments : []
  let fromInstance = []
  const raw = instanceInfo.value?.attachmentUrl
  if (typeof raw === 'string' && raw.trim().length > 0) {
    try { const arr = JSON.parse(raw); if (Array.isArray(arr)) fromInstance = arr }
    catch (e) { console.error(e); fromInstance = [] }
  }
  
  const all = [...fromJson, ...fromInstance]
  const seen = new Map()
  const result = []
  
  for (const item of all) {
    const norm = normalizeAttachment(item)
    if (!norm.url || norm.url === '#') continue
    
    const key = norm.url.trim().toLowerCase()
    if (!seen.has(key)) {
      seen.set(key, true)
      result.push(norm)
    }
  }
  return result
})

const sanitizeAttachmentUrl = (url) => {
  if (!url) return '#'
  const s = String(url).replace(/[`\s]+/g, '')
  const m = s.match(/https?:\/\/[^\s"']+/)
  return m ? m[0] : s
}

const isImage = (url) => {
  if (!url) return false
  return /\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i.test(String(url).split('?')[0])
}

const openImage = (att) => {
  if (!att) return
  
  // 如果传入的是字符串URL (兼容旧代码)
  if (typeof att === 'string') {
    const url = att
    const fileName = url.split('/').pop() || 'image.png'
    previewFile.value = {
      url: sanitizeAttachmentUrl(url),
      fileName: fileName,
      name: fileName
    }
  } else {
    // 传入的是附件对象
    previewFile.value = {
      ...att,
      url: sanitizeAttachmentUrl(att.url),
      fileName: att.fileName || att.name || 'image.png',
      name: att.fileName || att.name || 'image.png'
    }
  }
  showFileViewer.value = true
}

// Status Logic
const normalizeInstanceStatus = (val) => {
  if (val == null) return 'pending'
  if (typeof val === 'string') {
    const s = val.toLowerCase()
    if (['pending', 'start', '待审批'].includes(s)) return 'pending'
    if (['in_progress', 'inprogress', '审批中'].includes(s)) return 'in_progress'
    if (['approved', '审批通过', '已通过'].includes(s)) return 'approved'
    if (['rejected', '审批驳回', '已驳回'].includes(s)) return 'rejected'
    if (['cancelled', '已取消', '撤销'].includes(s)) return 'cancelled'
    return 'pending'
  }
  const n = Number(val)
  const map = { 1: 'pending', 2: 'in_progress', 3: 'approved', 4: 'rejected', 5: 'cancelled' }
  return map[n] || 'pending'
}

const currentStatusKey = computed(() => normalizeInstanceStatus(instanceInfo.value?.status || instanceInfo.value?.statusText))

const currentStatusTextZh = computed(() => {
  const map = {
    pending: '待审批',
    in_progress: '审批中',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[currentStatusKey.value] || '未知'
})

// Approval Logic
const getCurrentUser = () => {
  try {
    const stored = localStorage.getItem('userInfo')
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error(e)
  }
  return { name: '' }
}

const canApprove = computed(() => {
  const status = currentStatusKey.value
  if (status !== 'pending' && status !== 'in_progress') return false
  
  // If backend provides canApprove flag, use it
  if (instanceInfo.value?.canApprove !== undefined) return instanceInfo.value.canApprove
  
  const currentNodeName = instanceInfo.value?.currentNodeName || ''
  const userName = getCurrentUser().name
  
  if (!currentNodeName || !userName) return false
  
  const checkName = userName + "审批"
  return currentNodeName === checkName || currentNodeName === userName || currentNodeName.includes(userName)
})

const getNodeStatusText = (action) => {
  const map = { 0: '待审批', 1: '待审批', 2: '已通过', 3: '已驳回', 4: '已取消' }
  return map[Number(action)] || '待审批'
}

const nodeNumberClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-500'
  if (text.includes('驳回')) return 'bg-red-500'
  if (text.includes('取消')) return 'bg-gray-400'
  return 'bg-blue-500'
}

const actionBadgeClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-100 text-green-700'
  if (text.includes('驳回')) return 'bg-red-100 text-red-700'
  if (text.includes('取消')) return 'bg-gray-100 text-gray-700'
  return 'bg-blue-100 text-blue-700'
}

const ccUsersNormalized = computed(() => {
  const nodes = workflowNodes.value || []
  const result = []
  nodes.forEach(n => {
    if (Array.isArray(n.ccUsers) && n.ccUsers.length) {
      n.ccUsers.forEach(u => result.push(u))
    }
  })
  return result
})

const workflowNodesPreview = computed(() => {
  const nodes = workflowNodes.value || []
  const recs = approvalRecords.value || []
  
  return nodes.map(n => {
    const nodeRecs = recs.filter(x => x.nodeId === n.id)
    const lastRec = nodeRecs.sort((a,b) => new Date(b.processedAt) - new Date(a.processedAt))[0] || {}
    
    return {
      nodeName: n.nodeName,
      approvers: [{ approverName: n.approverName || n.approvers?.map(a=>a.name).join(',') }],
      action: lastRec.action,
      actionText: lastRec.actionText,
      processedAt: lastRec.processedAt,
      remarks: lastRec.remarks
    }
  })
})

const loadDetail = async () => {
  if (!applicationId.value) return
  loading.value = true
  try {
    const res = await approvalApi.getApplicationDetail(applicationId.value)
    if (res.success) {
      const app = res.data
      instanceInfo.value = app.instance || app
      approvalRecords.value = app.approvalRecords || []
      workflowNodes.value = app.workflowNodes || []
      
      const jsonData = app.instance?.jsonData || app.jsonData || app.applicationData || '{}'
      initialData.value = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData
    } else {
      error.value = res.message || '加载失败'
    }
  } catch (e) {
    error.value = '网络错误'
    console.error(e)
  } finally {
    loading.value = false
  }
}

const handleApprove = async () => {
  approving.value = true
  try {
    const res = await approvalApi.approveInstance(applicationId.value, { remarks: approvalComment.value })
    if (res.success) {
      toast.success('审批通过')
      checkAndPromptNext()
    } else {
      toast.error(res.message || '操作失败')
    }
  } catch (e) {
    toast.error('网络错误')
  } finally {
    approving.value = false
  }
}

const handleReject = async () => {
  if (!approvalComment.value) {
    toast.error('请输入驳回意见')
    return
  }
  approving.value = true
  try {
    const res = await approvalApi.rejectInstance(applicationId.value, { remarks: approvalComment.value })
    if (res.success) {
      toast.success('审批驳回')
      checkAndPromptNext()
    } else {
      toast.error(res.message || '操作失败')
    }
  } catch (e) {
    toast.error('网络错误')
  } finally {
    approving.value = false
  }
}

onMounted(() => {
  loadDetail()
})
</script>




